import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
    LayoutDashboard,
    Radio,
    Video,
    FileVideo,
    Share2,
    Settings,
    Sun,
    Moon,
    Plus,
    Bell,
    User,
    LogOut,
    Menu,
    X
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/broadcasts', icon: Radio, label: 'Broadcasts' },
    { path: '/recordings', icon: FileVideo, label: 'Recordings' },
    { path: '/social', icon: Share2, label: 'Social' },
    { path: '/settings', icon: Settings, label: 'Settings' },
];

export const DashboardLayout = () => {
    const { toggleTheme, isDark } = useTheme();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 h-16 border-b bg-card/95 backdrop-blur-sm" data-testid="main-header">
                <div className="flex items-center justify-between h-full px-4 lg:px-6">
                    {/* Left: Logo & Mobile Menu */}
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            data-testid="mobile-menu-btn"
                        >
                            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                        <div className="flex items-center gap-2" data-testid="logo">
                            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                <Video className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold hidden sm:inline">StreamHub</span>
                        </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => navigate('/studio')}
                            className="bg-primary hover:bg-primary/90 text-white gap-2"
                            data-testid="go-live-btn"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="hidden sm:inline">Go Live</span>
                        </Button>

                        <Button variant="ghost" size="icon" data-testid="notifications-btn">
                            <Bell className="w-5 h-5" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle-btn"
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                                        JD
                                    </div>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem>
                                    <User className="w-4 h-4 mr-2" />
                                    Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/settings')}>
                                    <Settings className="w-4 h-4 mr-2" />
                                    Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <div className="flex">
                {/* Sidebar */}
                <aside
                    className={`fixed lg:sticky top-16 left-0 z-40 w-64 h-[calc(100vh-64px)] bg-card border-r transform transition-transform duration-200 lg:transform-none ${
                        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                    }`}
                    data-testid="sidebar"
                >
                    <nav className="p-4 space-y-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                    `sidebar-link ${isActive ? 'active' : ''}`
                                }
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                <item.icon className="w-5 h-5" />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Sidebar Footer */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
                        <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-xl p-4">
                            <h4 className="font-semibold text-sm mb-1">Upgrade to Pro</h4>
                            <p className="text-xs text-muted-foreground mb-3">
                                Get unlimited streams and more features
                            </p>
                            <Button size="sm" className="w-full" variant="secondary">
                                Upgrade
                            </Button>
                        </div>
                    </div>
                </aside>

                {/* Overlay for mobile */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <main className="flex-1 min-h-[calc(100vh-64px)] p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
